import React, { useState, useLayoutEffect, useRef } from "react";

import { useFormInput } from "../../custom-hooks/useFormInput";
import { saveWidget, addBookmarkList } from "../../actions/layoutActions";
import { hideModal } from "../../actions/modalActions";
import Button from "../button/button";
import ClickableText from "../clickableText/ClickableText";
import IconText from "../IconText/IconText";
import Tooltip from "../tooltip/tooltip";
import Input from "../input/input";
import { FaTimes, FaRegPlusSquare } from "react-icons/fa";
import "./BookmarkList.scss";
import {
  eventCategories,
  recordEvent
} from "../../utils/googleAnalyticsEvents";
import { useAppSelector, useAppDispatch } from "../../hooks";

export interface EditBookmarkListProps {
  widget_id: string;
}

export default function EditBookmarkList(props: EditBookmarkListProps) {
  const dispatch = useAppDispatch();
  const bookmarkLists = useAppSelector(state => state.layouts.bookmarkLists);

  const bookmarkList =
    bookmarkLists &&
    bookmarkLists.find(
      bookmarkCategory => bookmarkCategory.widget_id === props.widget_id
    );

  const category = useFormInput(bookmarkList ? bookmarkList.category : "");
  const [bookmarks, setBookmarks] = useState(
    bookmarkList
      ? bookmarkList.bookmarks
      : [
          {
            name: "",
            url: ""
          }
        ]
  );
  const listNameRef = useRef<HTMLInputElement | null>(null);

  useLayoutEffect(() => {
    listNameRef.current?.focus();
  }, [listNameRef]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    recordEvent(
      "Bookmark List Config Saved",
      eventCategories.COMPONENT_CONFIGURATION,
      "Bookmark List Config Saved"
    );
    dispatch(saveWidget(props.widget_id));
    dispatch(
      addBookmarkList({
        widget_id: props.widget_id,
        category: category.value,
        bookmarks
      })
    );
    dispatch(hideModal());
  };

  const saveBookmarkName = (index: number, bookmarkName: string) => {
    const bookmarksCopy = bookmarks.slice();
    const bookmark = bookmarksCopy[index];
    bookmark.name = bookmarkName;
    bookmarksCopy[index] = bookmark;
    setBookmarks(bookmarksCopy);
  };

  const saveBookmarkUrl = (index: number, url: string) => {
    const bookmarksCopy = bookmarks.slice();
    const bookmark = bookmarksCopy[index];
    bookmark.url = url;
    bookmarksCopy[index] = bookmark;
    setBookmarks(bookmarksCopy);
  };

  const addNewBookmark = () => {
    recordEvent(
      "Bookmark Added to List",
      eventCategories.COMPONENT_CONFIGURATION,
      "Bookmark Added to List"
    );
    const bookmarksCopy = bookmarks.slice();
    bookmarksCopy.push({ name: "", url: "" });
    setBookmarks(bookmarksCopy);
  };

  const removeBookmark = (index: number) => {
    recordEvent(
      "Bookmark Removed to List",
      eventCategories.COMPONENT_CONFIGURATION,
      "Bookmark Removed to List"
    );
    const bookmarksCopy = bookmarks.slice();
    bookmarksCopy.splice(index, 1);
    setBookmarks(bookmarksCopy);
  };

  return (
    <div>
      <form onSubmit={onSubmit} className="bookmark-list-form">
        <Input
          label="List Name"
          ariaLabel="List Name Input"
          placeholder="List Name"
          id={`listName_${props.widget_id}`}
          type="text"
          name="listName"
          {...category}
          required
          reference={listNameRef}
          usePermanentAccent
        />
        <table className="bookmark-table">
          <thead>
            <tr className="header-label-row">
              <th>Friendly Name</th>
              <th>URL</th>
            </tr>
          </thead>
          <tbody>
            {bookmarks.map((bookmark, index) => {
              return (
                <tr key={index}>
                  <td>
                    <Input
                      ariaLabel="Bookmark Name Input"
                      id={`bookmarkName_${props.widget_id}_${index}`}
                      type="text"
                      name="bookmarkName"
                      value={bookmarks[index].name}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        saveBookmarkName(index, event.target.value)
                      }
                      required
                      usePermanentAccent
                    />
                  </td>
                  <td>
                    <Input
                      ariaLabel="Bookmark URL Input"
                      id={`bookmarkUrl_${props.widget_id}_${index}`}
                      type="text"
                      name="bookmarkUrl"
                      value={bookmarks[index].url}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        saveBookmarkUrl(index, event.target.value)
                      }
                      required
                      usePermanentAccent
                    />
                  </td>
                  <td>
                    <Tooltip
                      tooltipMessage="Remove Bookmark"
                      placement="right"
                      referenceStyle={{ display: "inline-block" }}
                    >
                      <FaTimes
                        onClick={() => removeBookmark(index)}
                        className="remove-bookmark"
                        tabIndex={0}
                        aria-label="Remove Bookmark"
                      />
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
            <tr className="add-bookmark-row">
              <td>
                <IconText
                  wrapperClassName="add-bookmark"
                  onClick={addNewBookmark}
                >
                  <FaRegPlusSquare
                    tabIndex={0}
                    aria-label="Add Bookmark"
                  />
                  <ClickableText>Add Bookmark</ClickableText>
                </IconText>
              </td>
            </tr>
          </tbody>
        </table>

        <Button type="submit" usePermanentAccent>
          Save Bookmark List
        </Button>
      </form>
    </div>
  );
}
